import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid2 as Grid,
  Button,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MosaicPlaceholder from "../components/MosaicPlaceholder";
import CourseBox from "../components/CourseBox";
import theme from "../styles/theme";
import RegistrationForm from "../components/RegistrationForm";
import registrationService from "../services/registrationService";
import CourseService from "../services/courseService";
import useNotification from "../hooks/useNotification";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Course from "../models/Course";

const StyledTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: "auto", // This pushes the button to the bottom
  backgroundColor: '#00537E'
}));

const scrollIntoView = (elementId: string) => {
  const elem = document.getElementById(elementId);
  if (elem) {
    var top = elem.offsetTop; //Getting Y of target element
    window.scrollTo(0, top);
  }
}

const CoursesPage: React.FC = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const queryClient = useQueryClient();
  const { showNotification,  } = useNotification();

  const [isRegistrationFormOpen, setIsRegistrationFormOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0)
  }, []);

  const fetchCoursesWithLog = async () => {
    console.log("Fetching courses...");
    const result = await CourseService.fetchCourses({
      limit: 500,
      visible: true,
    });
    console.log("Courses fetched:", result);
    if (result && result.courses && Array.isArray(result.courses)) {
      return result.courses;
    } else {
      console.error("courses is not an array or data is missing:", result);
      return [];
    }
  };

  const {
    data: onlineCourse,
    isLoading: isCoursesLoading,
  } = useQuery<Course, Error, Course, ["courses"]>({
    queryKey: ["courses"],
    queryFn: fetchCoursesWithLog,
    select: (courseData: any) => {
      if (courseData) {
        return courseData.filter((course: Course) => course.visible && course.type == "online")[0];
      } else {
        console.error(
          "courses is not an array or data is missing:",
          courseData
        );
        return [];
      }
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  // Mutation for course registration
  const registrationMutation = useMutation({
    mutationFn: (data: {
      courseId: number | null;
      formData: any;
      code: string | undefined;
    }) =>
      registrationService.registerForCourse(
        data.courseId,
        data.formData,
        data.code
      ),
    onSuccess: (response) => {
      if (response.status === 200) {
        queryClient.invalidateQueries({ queryKey: ["courses"] });
        showNotification(
          "Registration request submitted successfully",
          "success"
        );
      } else if (response.status === 409) {
        showNotification(
          `You are already registered for this course. Current status: ${response.data.status}`,
          "info"
        );
      } else if (response.status === 403) {
        showNotification(
          "Course is full. Please try another course or contact the administrator.",
          "error"
        );
      }
      handleRegistrationFormClose();
    },
    onError: () => {
      showNotification(
        "Failed to register for the course. Please try again.",
        "error"
      );
    },
  });

  const handleRegister = () => {
    setIsRegistrationFormOpen(true);
  };

  const handleRegistrationFormClose = () => {
    setIsRegistrationFormOpen(false);
  };

  const handleRegistrationFormSubmit = async (formData: any) => {
    registrationMutation.mutate({
      courseId: onlineCourse ? onlineCourse.id : null,
      formData: formData.submissionData,
      code: "",
    });
  };


  return (
    <Container maxWidth="lg" style={{ minHeight: "800px" }}>
      <MosaicPlaceholder />
      {isRegistrationFormOpen && (
        <RegistrationForm
          open={isRegistrationFormOpen}
          onClose={handleRegistrationFormClose}
          onSubmit={handleRegistrationFormSubmit}
          selectedCourseName={isCoursesLoading ? "" : onlineCourse?.name}
        />
      )}
      {isMobile ?
        <Grid container spacing={2} style={{ marginBottom: "25px", marginTop: "15px" }}>
          <Grid size={12} style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <StyledButton onClick={() => scrollIntoView("Partner Training Class")} variant="contained" color="primary" fullWidth>
            Partner Training Class
            </StyledButton>
          </Grid>
          <Grid size={12} style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <StyledButton onClick={() => scrollIntoView("Tactical")} variant="contained" color="primary" fullWidth>
              Tactical
            </StyledButton>
          </Grid>
          <Grid size={12} style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <StyledButton onClick={() => scrollIntoView("Online Partner Training")} variant="contained" color="primary" fullWidth>
            Online Partner Training
            </StyledButton>
          </Grid>
          <Grid size={12} style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <StyledButton onClick={() => scrollIntoView("Mini-Course")} variant="contained" color="primary" fullWidth>
              Mini-Course
            </StyledButton>
          </Grid>
          <Grid size={12} style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <StyledButton onClick={() => scrollIntoView("Dispatcher Partner Training")} variant="contained" color="primary" fullWidth>
              Dispatcher Partner Training
            </StyledButton>
          </Grid>
          <Grid size={12} style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <StyledButton onClick={() => scrollIntoView("Rail Tactics (Coming Soon)")} variant="contained" color="primary" fullWidth>
              Rail Tactics
            </StyledButton>
          </Grid>
        </Grid>
        : null}
      <Box sx={theme.custom.pageTitle}>
        <StyledTypography variant="body1" fontSize={"18px"}>
          Operation RAILSAFE offers a variety of in-person and online courses designed to 
          build critical partnerships, strengthen coordination and integration, enhance information
          sharing and collaboration, teach equipment familiarization and tactical response
          considerations, provide an overview on how to stay safe in a deadly rail environment, 
          and aid in anti-terrorism efforts designed to protect critical infrastructure.
        </StyledTypography>
        <StyledTypography variant="body1" fontSize={"18px"}>
          These courses are free to all Amtrak Police Department (APD) partner first 
          responder agencies – federal, state, local, territorial, and tribal law enforcement 
          to include K-9 and explosive ordnance divisions, transit police and rail security,  
          municipal and county sheriff’s departments, fire departments, dispatchers, emergency 
          medical services, coroners, and emergency management agencies.
        </StyledTypography>
        <StyledTypography variant="body1" fontSize={"18px"}>
          These courses are consistent with the National Infrastructure Protection Plan, 
          Transportation Sector Specific Plan, and the National Response Framework. They 
          are also aligned with the planning, prevention, protection, and community resilience 
          core capabilities outlined in the National Preparedness Goal.
        </StyledTypography>
        &nbsp;
      </Box>
      <Grid container spacing={2} sx={{ xs: 12, mt: "-30px" }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <CourseBox
            title="Partner Training Class"
            description="A one-and-a-half day in-person class focused on enabling APD partner agencies to respond to a range of terrorist threats. Benefits include an introduction to APD and its intelligence capabilities, general railroad safety, arriving on scene best practices, and two HSEEP compliant tabletop exercises."
            imageSrc="/images/LegacyClass.png"
            imagePosition="bottom top"
            registerLink="/register?courseType=legacy"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <CourseBox
            title="Tactical"
            description="A one-day hands-on learning experience designed to familiarize law enforcement patrol officers with approaching, breaching, boarding, and responding on Amtrak passenger train equipment during an active assailant incident. Taught at select locations around the country."
            imageSrc="/images/RAILSAFETacticalv2.jpg"
            registerLink="/register?courseType=tactical"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <CourseBox
            title="Online Partner Training"
            description="A one-hour, self-paced, web-based training course developed as a shortened version of the Partner Training Class. You can take this course at a time and location that is convenient for you."
            registerButtonTitle="Register for Course"
            imageSrc="/images/OnlineCourses.png"
            registerLink="/online-course"
            onClick={handleRegister}
          />
        </Grid>
      </Grid>
      <Grid
        size={{ xs: 12 }}
        container
        spacing={2}
        sx={{ mt: 2 }}
      >
        <Grid size={{ xs: 12, md: 4 }}>
          <CourseBox
            title="Mini-Course"
            description="A two-hour in-person law enforcement class developed for and given in jurisdictions with trouble spots, trends, or frequent strikes. Delivered by an APD officer at your venue and customized to your jurisdiction. Includes an APD capabilities briefing, general railroad safety, arriving on scene best practices, and local criminal codes."
            imageSrc="/images/Mini-Courses.png"
            registerLink="mailto:railsafe@amtrak.com"
            registerButtonTitle="Request Class"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <CourseBox
            title="Dispatcher Partner Training (Coming Soon)"
            description="A one-hour web-based course developed to build public safety dispatch professional’s awareness of railway hazards, first responder safety, and communications and actions necessary during incidents involving trains, stations, or other operations on or near the railroad right-of-way."
            imageSrc="/images/DispatcherTraining.png"
            registerLink="#"
            registerButtonTitle="Coming Soon"
            linkActive={false}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <CourseBox
            title="Rail Tactics (Coming Soon)"
            description="A two-day hands-on course designed for SWAT and other tactical teams."
            imageSrc="/images/RailTactics.png"
            registerLink="#"
            registerButtonTitle="Coming Soon"
            linkActive={false}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default CoursesPage;
